import * as XLSX from 'xlsx';
import { TYPE_TO_LEVEL } from './constants.js';
import { parseInitiatives, classifyTocAlignment } from './parsers.js';

// Extract year from reporting phase string (e.g. "IPSR 2023" → 2023)
function extractYear(phase) {
  if (!phase) return null;
  const match = phase.match(/(\d{4})/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Parse the NEW consolidated Excel file format.
 * Expected columns:
 * Result Code, id, Year, Status, Funding Type (W1W2 | W3Bilateral),
 * Initiative, Primary Submitter, Contributing Initiatives, Type,
 * Title, Description, Lead Contact Person, KP Type,
 * Gender Tag Level, Climate Tag Level, Nutrition Tag Level,
 * Biodiversity Tag Level, Poverty Tag Level, Is Key Result Story,
 * Lead Center, Contributing Centers, Non-pooled Projects, ToC Mapping,
 * Partners, Partners KP, Regions, Countries, Linked Results,
 * Evidence 1-6, Innovation Use Level, Readiness Level,
 * W3 Project Title, W3 Project Info
 */
export function parseResultsData(workbook) {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const data = XLSX.utils.sheet_to_json(sheet);

  return data.map(row => {
    // Detect format: V22 (explicit indicators) vs consolidated vs legacy
    if ('Program Code' in row && 'Result Type Group' in row) {
      return parseV22Row(row);
    }
    if ('Funding Type' in row) {
      return parseConsolidatedRow(row);
    }
    return parseLegacyRow(row);
  });
}

// Parse a row from the new consolidated format
function parseConsolidatedRow(row) {
  return {
    resultCode: row['Result Code'],
    id: row['id'],
    year: row['Year'] || extractYear(row['Reporting Phase']) || 2025,
    status: row['Status'],
    fundingType: row['Funding Type'], // 'W1W2' or 'W3Bilateral' — THE SOURCE OF TRUTH
    initiative: row['Initiative'],
    primarySubmitter: row['Primary Submitter'],
    contributingInitiatives: parseInitiatives(row['Contributing Initiatives']),
    type: row['Type'],
    level: TYPE_TO_LEVEL[row['Type']] || null,
    title: row['Title'] || '',
    description: row['Description'] || '',
    leadContact: row['Lead Contact Person'],
    kpType: row['KP Type'],
    genderLevel: row['Gender Tag Level'],
    climateLevel: row['Climate Tag Level'],
    nutritionLevel: row['Nutrition Tag Level'],
    biodiversityLevel: row['Biodiversity Tag Level'],
    povertyLevel: row['Poverty Tag Level'],
    isKRS: row['Is Key Result Story'],
    leadCenter: row['Lead Center'],
    contributingCenters: row['Contributing Centers'],
    nonPooledProjects: row['Non-pooled Projects'],
    tocMapping: row['ToC Mapping'],
    tocMappingType: classifyTocAlignment(row),
    partners: row['Partners'],
    partnersKP: row['Partners KP'],
    regions: row['Regions'],
    countries: row['Countries'],
    linkedResults: row['Linked Results'],
    evidence: [
      row['Evidence 1'],
      row['Evidence 2'],
      row['Evidence 3'],
      row['Evidence 4'],
      row['Evidence 5'],
      row['Evidence 6'],
    ].filter(Boolean),
    innovationUseLevel: row['Innovation Use Level'],
    readinessLevel: row['Readiness Level'],
    w3ProjectTitle: row['W3 Project Title'],
    w3ProjectInfo: row['W3 Project Info'],
    isPlanned: row['Is Planned?'] || null,
    indicatorUsed: row['Indicator used'] || null,
    actualAchieved: row['Actual Achieved'] != null ? row['Actual Achieved'] : null,
    // Innovation-specific structured fields (cols 51-69)
    innovationCharacterization: row['What would be the best way to characterize this innovation?:'],
    innovationTypology: row['Which of the below typologies best fits the nature of the innovation?:'],
    isVarietyBreed: row['Are you profiling a new or improved variety or breed?'],
    actorsDevUse: row['Actors Dev/Use Step 1'],
    innoUseActors: row['Inno Use Actors Information'],
    ipsrCoreInfo: row['IPSR Core Innovation Package Assess Information'],
    ipsrComplementaryInfo: row['IPSR Complementary Innovation Package Assess Information'],
    ipsrActors: row['IPSR Actors'],
    ipsrOrganizations: row['IPSR Organizations'],
    ipsrMeasures: row['IPSR Measures'],
    reportingPhase: row['Reporting Phase'] || null,
    discontinuedReason: row['Investment Discontinued Option'] || null,
  };
}

// Parse a row from the old (legacy) single-file format for backwards compatibility
function parseLegacyRow(row) {
  return {
    resultCode: row['Result code'] || row['Result Code'],
    id: row['id'],
    year: row['Year'] || extractYear(row['Reporting Phase']) || 2025,
    status: row['Status'],
    fundingType: 'source' in row
      ? (row['source'] === 'API' ? 'W3Bilateral' : 'W1W2')
      : (row['Non pooled projects'] || row['Non-pooled Project(s)'] ? 'W3Bilateral' : 'W1W2'),
    initiative: row['Initiative'] || '',
    primarySubmitter: row['Primary Submitter'] || row['Submitter'] || '',
    level: row['Level'] || TYPE_TO_LEVEL[row['Type'] || row['Indicator type']] || null,
    contributingInitiatives: parseInitiatives(row['Contributing initiatives'] || row['Contributing Initiative(s)']),
    type: row['Type'] || row['Indicator type'],
    title: row['Title'] || '',
    description: row['Description'] || row['Result Description'] || '',
    leadContact: row['Lead contact person'] || row['Lead Contact Person'],
    kpType: row['KP Type'],
    genderLevel: row['Gender level'] || row['Gender Tag Level'],
    climateLevel: row['Climate change level'] || row['Climate Tag Level'],
    nutritionLevel: row['Nutrition tag level'] || row['Nutrition Tag Level'],
    biodiversityLevel: row['Environmental biodiversity tag level'] || row['Environment and/or biodiversity Tag Level'],
    povertyLevel: row['Poverty reduction tag level'] || row['Poverty Tag Level'],
    isKRS: row['Is KRS'] || row['Is Key Result Story?'],
    leadCenter: row['Lead Center Institution'] || row['Primary Centers reporting the result'] || row['Lead Center'],
    contributingCenters: row['Contributing centers'] || row['Contributing Center(s)'],
    nonPooledProjects: row['Non pooled projects'] || row['Non-pooled Project(s)'],
    tocMapping: row['TOC results'] || row['ToC Mapping'],
    tocMappingType: classifyTocAlignment(row),
    partners: row['Partners'] || row['Partners (with delivery type) for non-KP results'],
    partnersKP: row['Additional partners (with delivery type) for KP results'] || row['Partners KP'],
    regions: row['CGIAR regions'] || row['Regions'],
    countries: row['Countries'],
    linkedResults: row['Linked results'] || row['Linked Results'],
    evidence: [
      row['Evidence 1'],
      row['Evidence 2'],
      row['Evidence 3'],
      row['Evidence 4'],
      row['Evidence 5'],
      row['Evidence 6'],
    ].filter(Boolean),
    actors: row['Actors'],
    innovationUseLevel: row['Innovation Use Level'],
    readinessLevel: row['Readiness Level'],
    w3ProjectTitle: row['W3 Project Title'],
    w3ProjectInfo: row['W3 Project Info'],
    indicatorUsed: row['Indicator used'] || row['Indicator Used'] || null,
    actualAchieved: row['Actual Achieved'] != null ? row['Actual Achieved'] : (row['Actual achieved'] != null ? row['Actual achieved'] : null),
    // Innovation-specific structured fields (cols 51-69)
    innovationCharacterization: row['What would be the best way to characterize this innovation?:'] || row['Innovation Characterization'],
    innovationTypology: row['Which of the below typologies best fits the nature of the innovation?:'] || row['Innovation Typology'],
    isVarietyBreed: row['Are you profiling a new or improved variety or breed?'] || row['Is Variety Breed'],
    actorsDevUse: row['Actors Dev/Use Step 1'] || row['Actors'],
    innoUseActors: row['Inno Use Actors Information'],
    ipsrCoreInfo: row['IPSR Core Innovation Package Assess Information'],
    ipsrComplementaryInfo: row['IPSR Complementary Innovation Package Assess Information'],
    ipsrActors: row['IPSR Actors'],
    ipsrOrganizations: row['IPSR Organizations'],
    ipsrMeasures: row['IPSR Measures'],
    reportingPhase: row['Reporting Phase'] || null,
    discontinuedReason: row['Investment Discontinued Option'] || null,
  };
}

// ---------------------------------------------------------------------------
// V22 indicator parsing helpers
// ---------------------------------------------------------------------------

/**
 * Parse cleaned indicator columns produced by preprocess_v22.py.
 * These columns have program prefixes already stripped and values aligned.
 * Each column is newline-separated, positionally aligned across the three.
 *
 * @param {string} textsStr   - "% of targeted actors...\nNumber of KP"
 * @param {string} programsStr - "SP05\nSP05"
 * @param {string} valuesStr   - "30.0\n12.0"
 * @returns {{ indicators: Array<{program, text}>, achieved: Array<{program, value}> }}
 */
function parseCleanedIndicatorColumns(textsStr, programsStr, valuesStr) {
  const texts = (textsStr || '').split('\n').map(s => s.trim());
  const programs = (programsStr || '').split('\n').map(s => s.trim());
  const values = (valuesStr || '').split('\n').map(s => s.trim());
  const indicators = [];
  const achieved = [];
  const maxLen = Math.max(texts.length, programs.length, values.length);
  for (let i = 0; i < maxLen; i++) {
    const prog = programs[i] || '';
    if (!prog) continue; // skip entries with no program code
    indicators.push({ program: prog, text: texts[i] || '' });
    const val = parseFloat(values[i]);
    achieved.push({ program: prog, value: isNaN(val) ? null : Math.max(val, 0) });
  }
  return { indicators, achieved };
}

/**
 * Parse program-prefixed indicator lines from V22 columns like "Primary Indicator used".
 * Format: one entry per line — "SP08 - Number of knowledge products"
 * Returns: [{ program: 'SP08', text: 'Number of knowledge products' }, ...]
 */
function parseProgramIndicatorLines(rawValue) {
  if (!rawValue) return [];
  const text = String(rawValue);
  return text.split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const m = line.match(/^(SP\d{2}|INIT-\d+|PLAT-?\d*|SGP-?\d*)\s*-\s*(.+)$/);
      if (m) return { program: m[1], text: m[2].trim() };
      return null;
    })
    .filter(Boolean);
}

/**
 * Parse program-prefixed achieved values from V22 columns like "Primary Actual Achieved".
 * Format: one entry per line — "SP08 - 238"
 * Returns: [{ program: 'SP08', value: 238 }, ...]
 * Positionally aligned with parseProgramIndicatorLines output.
 */
function parseProgramAchievedLines(rawValue) {
  if (rawValue == null) return [];
  const text = String(rawValue);
  return text.split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const m = line.match(/^(SP\d{2}|INIT-\d+|PLAT-?\d*|SGP-?\d*)\s*-\s*(.+)$/);
      if (m) {
        const val = parseFloat(m[2].trim());
        return { program: m[1], value: isNaN(val) ? null : Math.max(val, 0) };
      }
      return null;
    })
    .filter(Boolean);
}

// Parse a row from V22 format (explicit primary/contributor indicator columns)
function parseV22Row(row) {
  // Normalize funding type: 'W1/W2' → 'W1W2', 'W3/Bilat' → 'W3Bilateral'
  let fundingType = row['Result Type Group'] || '';
  if (/w1.*w2/i.test(fundingType)) fundingType = 'W1W2';
  else if (/w3|bilat/i.test(fundingType)) fundingType = 'W3Bilateral';

  return {
    resultCode: row['Result Code'],
    id: row['id'],
    year: row['Year'] || extractYear(row['Reporting Phase']) || 2025,
    status: row['Status'],
    fundingType,
    initiative: row['Program Code'] || '',
    primarySubmitter: row['Primary Submitter'] || '',
    contributingInitiatives: parseInitiatives(row['Contributing Initiative(s)'] || row['Contributing Initiatives']),
    type: row['Indicator type'] || row['Type'],
    level: TYPE_TO_LEVEL[row['Indicator type'] || row['Type']] || null,
    title: row['Result Title'] || row['Title'] || '',
    description: row['Result Description'] || row['Description'] || '',
    leadContact: row['Lead Contact Person'] || row['Lead contact person'],
    kpType: row['KP Type'],
    genderLevel: row['Gender Tag Level'],
    climateLevel: row['Climate Tag Level'],
    nutritionLevel: row['Nutrition Tag Level'],
    biodiversityLevel: row['Environment and/or biodiversity Tag Level'] || row['Biodiversity Tag Level'],
    povertyLevel: row['Poverty Tag Level'],
    isKRS: row['Is Key Result Story?'] || row['Is Key Result Story'],
    leadCenter: row['Lead Center Institution'] || row['Primary Centers reporting the result'] || row['Lead Center'],
    contributingCenters: row['Contributing Center(s)'] || row['Contributing Centers'],
    nonPooledProjects: row['Non-pooled Project(s)'] || row['Non-pooled Projects'],
    tocMapping: row['ToC Mapping / ToC Details'] || row['ToC Mapping'] || row['ToC Details (down to indicator)'],
    tocMappingType: classifyTocAlignment(row),
    partners: row['Partners (with delivery type) for non-KP results'] || row['Partners'],
    partnersKP: row['Additional partners (with delivery type) for KP results'] || row['Partners KP'],
    regions: row['Regions'] || row['CGIAR regions'],
    countries: row['Countries'],
    linkedResults: row['Linked Results'] || row['Linked results'],
    evidence: [
      row['Evidence 1'],
      row['Evidence 2'],
      row['Evidence 3'],
      row['Evidence 4'],
      row['Evidence 5'],
      row['Evidence 6'],
    ].filter(Boolean),
    innovationUseLevel: row['Innovation Use Level'],
    readinessLevel: row['Innovation Readiness Level'] || row['Readiness Level'],
    w3ProjectTitle: row['Project Title'] || row['W3 Project Title'],
    w3ProjectInfo: row['Bilateral projects'] || row['W3 Project Info'],
    // Preserve raw indicator text for export (V22 structured fields used for traffic lights)
    indicatorUsed: row['Indicator used'] || null,
    actualAchieved: row['Actual Achieved'] != null ? row['Actual Achieved'] : null,
    // V22 explicit indicator fields (structured)
    // Prefer cleaned columns from preprocess_v22.py when available
    ...(() => {
      const hasCleaned = 'Primary Achieved Value' in row;
      if (hasCleaned) {
        const pri = parseCleanedIndicatorColumns(
          row['Primary Indicator Text'],
          row['Primary Indicator Programs'],
          row['Primary Achieved Value']
        );
        const con = parseCleanedIndicatorColumns(
          row['Contributor Indicator Text'],
          row['Contributor Indicator Programs'],
          row['Contributor Achieved Value']
        );
        return {
          primaryIndicators: pri.indicators,
          primaryAchieved: pri.achieved,
          contributorIndicators: con.indicators,
          contributorAchieved: con.achieved,
        };
      }
      // Fallback: parse from raw program-prefixed columns
      return {
        primaryIndicators: parseProgramIndicatorLines(row['Primary Indicator used']),
        primaryAchieved: parseProgramAchievedLines(row['Primary Actual Achieved']),
        contributorIndicators: parseProgramIndicatorLines(row['Contributor Indicator used']),
        contributorAchieved: parseProgramAchievedLines(row['Contributor Actual Achieved']),
      };
    })(),
    // New V22 field for global filter
    reportingPhase: row['Reporting Phase'] || null,
    pdfLink: row['PDF Link'] || null,
    // Innovation-specific structured fields
    innovationCharacterization: row['What would be the best way to characterize this innovation?:'] || row['Innovation Characterization'],
    innovationTypology: row['Which of the below typologies best fits the nature of the innovation?:'] || row['Which of the below typologies best fits the nature of the innovation? (Innovation typology)'] || row['Innovation Typology'],
    isVarietyBreed: row['Are you profiling a new or improved variety or breed?'] || row['Is Variety Breed'],
    actorsDevUse: row['Actors Dev/Use Step 1'] || row['Actors'],
    innoUseActors: row['Inno Use Actors Information'],
    ipsrCoreInfo: row['IPSR Core Innovation Package Assess Information'],
    ipsrComplementaryInfo: row['IPSR Complementary Innovation Package Assess Information'],
    ipsrActors: row['IPSR Actors'],
    ipsrOrganizations: row['IPSR Organizations'],
    ipsrMeasures: row['IPSR Measures'],
    isPlanned: row['Is Planned?'] || null,
    discontinuedReason: row['Investment Discontinued Option'] || null,
  };
}

// Program name → SP code mapping for consolidated ToC file
const PROGRAM_NAME_TO_CODE = {
  'Breeding for Tomorrow': 'SP01',
  'Sustainable Farming': 'SP02',
  'Sustainable Animal and Aquatic Foods': 'SP03',
  'Multifunctional Landscapes': 'SP04',
  'Better Diets and Nutrition': 'SP05',
  'Climate Action': 'SP06',
  'Policy Innovations': 'SP07',
  'Food Frontiers and Security': 'SP08',
  'Scaling for Impact': 'SP09',
  'Gender Equality and Inclusion': 'SP10',
  'Capacity Sharing': 'SP11',
  'Digital Transformation': 'SP12',
  'Genebank': 'SP13',
};

// Parse consolidated TOC file (all programs in one workbook, split by Program/Accelerator column)
export function parseConsolidatedTocData(workbook) {
  const sheets = {};
  workbook.SheetNames.forEach(sheetName => {
    const sheet = workbook.Sheets[sheetName];
    sheets[sheetName] = XLSX.utils.sheet_to_json(sheet);
  });

  const resultsFramework = sheets['Result Framework'] || sheets['Results Framework'] || [];

  // Group rows by Program/Accelerator
  const grouped = {};
  for (const row of resultsFramework) {
    const programName = row['Program/Accelerator'];
    if (!programName) continue;
    if (!grouped[programName]) grouped[programName] = [];
    grouped[programName].push(row);
  }

  // Read 2030 Outcomes sheet once (consolidated uses "2030 Outcomes" plural)
  const outcomes2030AllRows = sheets['2030 Outcomes'] || sheets['2030 Outcome'] || [];

  const result = {};

  for (const [programName, rows] of Object.entries(grouped)) {
    const code = PROGRAM_NAME_TO_CODE[programName];
    if (!code) {
      console.warn(`Unknown program name in consolidated ToC: "${programName}"`);
      continue;
    }

    const areasOfWork = [...new Set(rows.map(r => r['AOW Title']).filter(Boolean))].sort((a, b) => a.localeCompare(b));

    const indicators = rows.map(row => ({
      aow: row['AOW Title'],
      resultType: row['Result type (Intermediate outcome or high level output)'],
      shortTitle: row['Result Short title'],
      statement: row['Result Statement'],
      indicator: row['Indicator'],
      type: row['Type'],
      unit: row['Unit of measurement'],
      dataSource: row['Data source'],
      baseline: row['2025 - 2030 Baseline value'],
      target2025: row['2025 target value'],
      target2026: row['2026 target value'],
      target2027: row['2027 target value'],
      target2028: row['2028 target value'],
      target2029: row['2029 target value'],
      target2030: row['2030 target value'],
      totalTarget: row['2025-2030 target value'],
    }));

    // Parse 2030 Outcomes for THIS program (filter by Program/Accelerator)
    // Some outcomes link to multiple AoWs, so collect all unique AoW titles per outcome
    const outcomes2030Map = new Map();
    outcomes2030AllRows
      .filter(r => r['Program/Accelerator'] === programName && r['Result Short title']
        && !(r['Result type (Intermediate outcome or high level output)'] || '').toLowerCase().includes('intermediate'))
      .forEach(r => {
        const title = r['Result Short title'] || '';
        if (!title) return;
        const existing = outcomes2030Map.get(title);
        const aowTitle = r['AOW Title'] || '';
        if (existing) {
          if (aowTitle && !existing.aows.includes(aowTitle)) {
            existing.aows.push(aowTitle);
          }
        } else {
          outcomes2030Map.set(title, {
            aows: aowTitle ? [aowTitle] : [],
            title,
            statement: r['Result Statement'] || '',
          });
        }
      });
    const outcomes2030 = [...outcomes2030Map.values()];

    result[code] = {
      programCode: code,
      areasOfWork,
      indicators,
      outcomes2030,
      sheets,
    };
  }

  return result;
}

// Parse TOC (Theory of Change) data — single program per workbook (legacy individual files)
export function parseTocData(workbook, programCode) {
  const sheets = {};

  workbook.SheetNames.forEach(sheetName => {
    const sheet = workbook.Sheets[sheetName];
    sheets[sheetName] = XLSX.utils.sheet_to_json(sheet);
  });

  // Extract Results Framework
  const resultsFramework = sheets['Result Framework'] || sheets['Results Framework'] || [];

  // Extract Areas of Work
  const areasOfWork = [...new Set(resultsFramework.map(r => r['AOW Title']).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  // Extract indicators with targets
  const indicators = resultsFramework.map(row => ({
    aow: row['AOW Title'],
    resultType: row['Result type (Intermediate outcome or high level output)'],
    shortTitle: row['Result Short title'],
    statement: row['Result Statement'],
    indicator: row['Indicator'],
    type: row['Type'],
    unit: row['Unit of measurement'],
    dataSource: row['Data source'],
    baseline: row['2025 - 2030 Baseline value'],
    target2025: row['2025 target value'],
    target2026: row['2026 target value'],
    target2027: row['2027 target value'],
    target2028: row['2028 target value'],
    target2029: row['2029 target value'],
    target2030: row['2030 target value'],
    totalTarget: row['2025-2030 target value'],
  }));

  // Parse 2030 Outcomes (some outcomes link to multiple AoWs)
  const outcomes2030Sheet = sheets['2030 Outcomes'] || sheets['2030 Outcome'] || [];
  const outcomes2030Map = new Map();
  outcomes2030Sheet
    .filter(r => (r['2030 Outcome title'] || r['Result Short title'])
      && !(r['Result type (Intermediate outcome or high level output)'] || '').toLowerCase().includes('intermediate'))
    .forEach(r => {
      const title = r['2030 Outcome title'] || r['Result Short title'] || '';
      if (!title) return;
      const existing = outcomes2030Map.get(title);
      const aowTitle = r['AOW Title'] || '';
      if (existing) {
        if (aowTitle && !existing.aows.includes(aowTitle)) {
          existing.aows.push(aowTitle);
        }
      } else {
        outcomes2030Map.set(title, {
          aows: aowTitle ? [aowTitle] : [],
          title,
          statement: r['2030 Outcome Statement'] || r['Result Statement'] || '',
        });
      }
    });
  const outcomes2030 = [...outcomes2030Map.values()];

  // Parse Impact Areas
  const iaSheet = sheets['IA'] || [];
  const impactAreas = iaSheet
    .filter(r => r['IA'])
    .map(r => ({
      name: r['IA'],
      globalTarget: r['Global Target'] || '',
      indicator: r['Indicator'] || '',
      description: r['Description'] || '',
    }));

  return {
    programCode,
    areasOfWork,
    indicators,
    outcomes2030,
    impactAreas,
    sheets,
  };
}
